import React, { useEffect, useState } from "react";
import { X, Globe2 } from "lucide-react";
import { useI18n } from "@/contexts/I18nContext";
import { REGIONS, Flag } from "./LanguageSelector";

const STORAGE_KEY = "akm_geo_dismissed";

/**
 * Geo-aware regional suggestion banner.
 * If the visitor's detected country differs from the active language,
 * a small non-intrusive banner suggests switching. Dismissal is persisted
 * so the banner never re-appears for that user.
 */
export default function GeoBanner() {
  const { lang, setLang } = useI18n();
  const [suggestion, setSuggestion] = useState(null);

  useEffect(() => {
    // Don't fetch if already dismissed
    try {
      if (localStorage.getItem(STORAGE_KEY)) return;
    } catch { /* ignore */ }

    let cancelled = false;
    (async () => {
      try {
        const res = await fetch("https://ipapi.co/json/", { cache: "no-store" });
        if (!res.ok) return;
        const data = await res.json();
        if (cancelled) return;
        const cc = (data.country_code || "").toUpperCase();
        // Map detected country → supported language code
        const region = REGIONS.find((r) => r.flag === cc);
        if (region && region.code !== lang) {
          setSuggestion(region);
        }
      } catch { /* network blocked / failed — silently skip */ }
    })();
    return () => { cancelled = true; };
  }, [lang]);

  function dismiss() {
    try { localStorage.setItem(STORAGE_KEY, "1"); } catch { /* ignore */ }
    setSuggestion(null);
  }

  function accept() {
    if (suggestion) setLang(suggestion.code);
    dismiss();
  }

  if (!suggestion) return null;

  return (
    <div
      role="dialog"
      aria-live="polite"
      data-testid="geo-banner"
      className="fixed inset-x-3 bottom-3 z-50 mx-auto max-w-[420px] overflow-hidden rounded-2xl border border-[#E2E8F0] bg-white shadow-[0_24px_60px_-20px_rgba(11,18,32,0.35)] sm:inset-x-auto sm:right-4 sm:bottom-4 sm:mx-0"
    >
      <span
        className="absolute inset-x-0 top-0 h-[2px] bg-gradient-to-r from-[#FF9A3D] via-[#F26322] to-[#EA580C]"
        aria-hidden
      />
      <div className="flex items-start gap-3 p-4">
        <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-[#FFF1E5] text-[#EA580C]">
          <Globe2 size={17} />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 text-[12px] font-semibold text-[#0F172A]">
            <Flag code={suggestion.flag} />
            <span>Looks like you&apos;re visiting from {suggestion.title}.</span>
          </div>
          <p className="mt-1 text-[12px] leading-[1.55] text-[#64748B]">
            Switch to your local region for a better experience?
          </p>
          <div className="mt-3 flex items-center gap-2">
            <button
              type="button"
              onClick={accept}
              className="inline-flex items-center gap-1.5 rounded-full bg-[#F26322] px-3.5 py-1.5 text-[12px] font-semibold text-white transition-colors hover:bg-[#EA580C]"
              data-testid="geo-banner-accept"
            >
              <Flag code={suggestion.flag} /> Switch to {suggestion.title}
            </button>
            <button
              type="button"
              onClick={dismiss}
              className="rounded-full border border-[#E2E8F0] bg-white px-3.5 py-1.5 text-[12px] font-semibold text-[#0F172A] transition-colors hover:border-[#CBD5E1]"
              data-testid="geo-banner-dismiss"
            >
              Stay here
            </button>
          </div>
        </div>
        <button
          type="button"
          onClick={dismiss}
          aria-label="Dismiss"
          className="grid h-6 w-6 shrink-0 place-items-center rounded-md text-[#94A3B8] hover:bg-[#F1F5F9] hover:text-[#0F172A]"
        >
          <X size={14} />
        </button>
      </div>
    </div>
  );
}
